/** @format — Expected document cards for shipment drawer */

import { useState } from "react";
import { FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDateLabel, formatUploadType } from "../utils/documentFormatters";
import type { AwaitingDocumentRow } from "../../shipments/hooks/useShipmentDocuments";

function ExpectedCardPreview({
  documentUrl,
  label,
}: {
  documentUrl: string | null;
  label: string;
}) {
  const [failed, setFailed] = useState(false);

  if (documentUrl && !failed) {
    return (
      <img
        src={documentUrl}
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-top"
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
      />
    );
  }

  return <FileText className="h-8 w-8 text-neutral-300" strokeWidth={1.5} aria-hidden />;
}

export default function DocumentsExpectedGrid({
  items,
  onViewDocument,
}: {
  items: AwaitingDocumentRow[];
  onViewDocument?: (url: string) => void;
}) {
  if (items.length === 0) {
    return <p className="py-12 text-center text-sm text-neutral-500">No documents in this tab.</p>;
  }

  return (
    <div className="grid grid-cols-3 gap-2">
      {items.map((item) => {
        const label = formatUploadType(item.uploadType);
        const documentUrl = item.documentUrl ?? item.fileUrl ?? null;
        const status = item.exists
          ? item.receivedAt
            ? `Received ${formatDateLabel(item.receivedAt)}`
            : "Received"
          : "Pending";
        const canOpen = item.exists && Boolean(documentUrl);

        return (
          <article
            key={item.uploadType}
            className={cn(
              "flex h-[180px] min-w-0 flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white",
              !item.exists && "opacity-60",
              canOpen && "cursor-pointer",
            )}
            onClick={() => {
              if (canOpen && documentUrl) onViewDocument?.(documentUrl);
            }}
          >
            <div className="flex min-h-0 flex-1 items-center justify-center bg-neutral-50 px-2 pt-2">
              <div className="relative h-full min-h-0 w-full overflow-hidden rounded-tl-lg rounded-tr-lg bg-neutral-50">
                <div className="flex h-full w-full items-center justify-center">
                  <ExpectedCardPreview documentUrl={documentUrl} label={label} />
                </div>
              </div>
            </div>
            <div className="shrink-0 p-2">
              <p className="truncate text-xs font-medium text-[#0a0a0a]" title={label}>
                {label}
              </p>
              <p className="mt-1 truncate text-[10px] text-[#475367]">{status}</p>
            </div>
          </article>
        );
      })}
    </div>
  );
}
